import React from 'react';
import { AlertCircle, TriangleAlert } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatEuro } from '@/lib/format';
import { IMPORT_TYPES, TENDER_COMPONENTS } from '@/lib/estimating/templates';
import { EST_IDS } from '@/constants/testIds/estimating';

export function ValidationList({ errors = [], warnings = [] }) {
  if (!errors.length && !warnings.length) return null;
  const render = (list, tone, Icon, tid, title) => (
    <div data-testid={tid} className={`rounded-md border px-3 py-2 text-sm ${tone}`}>
      <div className="font-medium flex items-center gap-1"><Icon size={14} /> {title} ({list.length})</div>
      <ul className="mt-1 max-h-40 overflow-y-auto text-xs space-y-0.5 pl-5 list-disc">
        {list.slice(0, 50).map((e, i) => <li key={i}>{e.row ? `Row ${e.row}: ` : ''}{e.message}</li>)}
        {list.length > 50 && <li>…and {list.length - 50} more</li>}
      </ul>
    </div>
  );
  return (
    <div className="space-y-2">
      {errors.length > 0 && render(errors, 'border-red-200 bg-red-50 text-red-900', AlertCircle, EST_IDS.validationErrors, 'Errors — fix these in Excel and re-upload')}
      {warnings.length > 0 && render(warnings, 'border-amber-200 bg-amber-50 text-amber-900', TriangleAlert, EST_IDS.validationWarnings, 'Warnings')}
    </div>
  );
}

export function lineColumns(importType) {
  if (importType === 'tender_summary') {
    return [
      { key: 'component', header: 'Component', render: (r) => TENDER_COMPONENTS.find((c) => c.key === r.component)?.label || r.component_label || r.component },
      { key: 'description', header: 'Description' },
      { key: 'amount', header: 'Amount', num: true },
    ];
  }
  const cols = IMPORT_TYPES[importType].columns.map((c) => ({ key: c.key, header: c.header, num: c.type === 'number' }));
  return cols;
}

export function PreviewTable({ parsed, limit = 15 }) {
  const cols = lineColumns(parsed.import_type);
  const rows = parsed.rows.slice(0, limit);
  return (
    <div data-testid={EST_IDS.previewTable} className="rounded-lg border border-border bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-xs">Row</TableHead>
            {cols.map((c) => <TableHead key={c.key} className={`text-xs ${c.num ? 'text-right' : ''}`}>{c.header}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.source_row}>
              <TableCell className="text-xs text-muted-foreground">{r.source_row}</TableCell>
              {cols.map((c) => (
                <TableCell key={c.key} className={`text-xs ${c.num ? 'text-right tabular-nums' : ''}`}>
                  {c.render ? c.render(r) : c.num ? (r[c.key] != null ? (c.key === 'quantity' ? r[c.key] : formatEuro(r[c.key])) : '') : (r[c.key] ?? '')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {parsed.rows.length > limit && <div className="px-4 py-2 text-xs text-muted-foreground">Showing first {limit} of {parsed.rows.length} rows</div>}
    </div>
  );
}
